<template>
  <div
    :class="{
      smooth: true,
      [`screen--${inScreen}`]: inScreen,
    }"
    :data-value="modelValue"
  >
    <div
      class="smooth__block"
      :style="{
        height: wrapperHeight,
        cursor: wrapperCursor,
      }"
      @mousewheel="navigateWheel"
    >
      <div class="smooth__overlay">
        <div></div>
        <div
          class="active"
          :style="{
            height: itemHeight,
          }"
        ></div>
        <div :style="{}"></div>
      </div>
      <div
        ref="wrapper"
        :class="{
          smooth__wrapper: true,
          'smooth__wrapper--grabbing': grabbing,
        }"
        :style="{
          transform: `translate3d(0, ${itemTransform}px, 0)`,
        }"
        @mousedown="dragStart"
        @touchstart.passive="dragStart"
      >
        <div
          v-for="(item, i) in data"
          :key="i"
          :style="{
            height: itemHeight,
            flex: `0 0 ${itemHeight}`,
          }"
          class="smooth__item"
        >
          <div class="smooth__inner">{{ item.label }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { __event_y__ } from "@/includes/helper";

export default {
  props: {
    modelValue: {
      default: 0,
      type: Number,
    },
    data: {
      type: Array,
      default: () => [],
    },
    height: {
      default: 42,
      type: Number,
    },
    inScreen: {
      default: 3,
      type: Number,
      validator: (value) => {
        return value === 2 || value % 2 != 0;
      },
    },
    dragDistance: {
      default: 10,
      type: Number,
    },
  },
  data() {
    return {
      current: -Math.floor(this.inScreen / 2),
      grabbing: false,
      dragState: {
        end: 0,
        start: 0,
      },
    };
  },
  mounted() {
    let indexVal = this.data.findIndex((f) => f.value == this.modelValue);
    this.goto(indexVal);

    document.body.addEventListener("mouseup", this.dragEnd);
    document.body.addEventListener("mousemove", this.dragMove);
    document.body.addEventListener("mouseleave", this.dragEnd);
    document.body.addEventListener("touchend", this.dragEnd, false);
    document.body.addEventListener("touchmove", this.dragMove, false);
    document.body.addEventListener("touchcancel", this.dragEnd, false);
  },
  methods: {
    dragStart(event) {
      this.dragState.start = __event_y__(event);
      this.grabbing = true;
    },
    dragMove(event) {
      if (!this.grabbing) return;
      this.dragState.end = __event_y__(event);
      this.$refs.wrapper.style.transform = `translate3d(0, ${this.dragState
        .end -
        this.dragState.start +
        this.itemTransform}px, 0)`;
    },
    navigateWheel(event) {
      const { wheelDelta } = event;
      if (wheelDelta < 0) this.next();
      else if (wheelDelta > 0) this.back();
    },
    dragEnd() {
      if (
        !this.grabbing ||
        Math.abs(this.dragState.end - this.dragState.start) < this.dragDistance
      ) {
        return this.resetDragState();
      }
      this.goto(
        -Math.round((this.dragState.end - this.dragState.start) / this.height)
      );
      this.resetDragState();
    },
    goto(index) {
      this.current += index;
      if (this.current < -this.border) this.current = -this.border;
      if (this.current > this.bottomBorder) this.current = this.bottomBorder;
      this.value = this.current + this.border;
    },
    back() {
      this.goto(-1);
    },
    next() {
      this.goto(1);
    },
    resetDragState() {
      this.dragState.end = 0;
      this.dragState.start = 0;
      this.grabbing = false;
    },
  },
  computed: {
    itemHeight() {
      return `${this.height}px`;
    },
    itemTransform() {
      return this.current * -this.height;
    },
    wrapperHeight() {
      return `${this.height * this.inScreen}px`;
    },
    wrapperCursor() {
      return this.grabbing ? "grabbing" : "grab";
    },
    border() {
      return Math.floor(this.inScreen / 2);
    },
    bottomBorder() {
      return (
        this.data.length -
        this.inScreen +
        this.border -
        (this.inScreen % 2 == 0 ? 1 : 0)
      );
    },
    value: {
      get() {
        return this.modelValue;
      },
      set(value) {
        let val = this.data[value].value;
        this.$emit("update:modelValue", val);
      },
    },
  },
  watch: {
    modelValue(val) {
      let indexVal = this.data.findIndex((f) => f.value == val);
      if (indexVal !== this.current + this.border)
        this.goto(indexVal - (this.current + this.border));
    },
  },
};
</script>

<style scoped>
.smooth {
  position: relative;

  display: flex;
  flex-wrap: nowrap;

  user-select: none;
  -webkit-user-drag: none;
}

.smooth__block {
  position: relative;

  overflow: hidden;
}

.smooth__wrapper {
  display: flex;
  flex-direction: column;
}

.smooth__wrapper:not(.smooth__wrapper--grabbing) {
  transition: transform 0.3s ease;
}

.smooth__item {
  position: relative;

  display: flex;
  align-items: center;
  justify-content: center;

  font-size: 16px;
  color: #424242;
  letter-spacing: 0.51px;
}

.smooth__inner {
  padding-left: 10px;
  padding-right: 10px;
}

.smooth__overlay {
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 2;
  position: absolute;

  display: flex;
  flex-direction: column;

  width: 100%;
  height: 100%;

  pointer-events: none;
}

.smooth__overlay::after,
.smooth__overlay::before {
  content: "";

  flex: 1;

  background-color: rgba(255, 255, 255, 0.7);
}

.smooth__overlay .active {
  border: 1px solid var(--color-border-alt);
  border-width: 1px 0 1px 0;
}

.screen--2 .smooth__overlay::after {
  display: none;
}
</style>
